import { format, parseISO } from "date-fns";
import type {
  ReviewOverallResponse,
  ReviewOverTimeResponse,
  ReviewOverTimeRow,
} from "@/types/analytics-reviews";
import type { TenantReviewsTableRow } from "@/types/analytics-tenant";
import {
  computeReviewsKpiDeltas,
  type ReviewsKpiDelta,
} from "@/components/pages/analytics/front-desk/components/reviews/reviewsKpiDeltas";
import { mapOverallToSummary } from "@/components/pages/analytics/front-desk/components/reviews/reviewsTabMappers";
import type {
  ReviewsKpiDeltas,
  ReviewsSummary,
} from "@/components/pages/analytics/front-desk/components/reviews/types";
import type {
  AdminHotelMetricRow,
  AdminMetricCell,
} from "../types/adminHotelMetricTable";

const TABLE_DELTA_SUFFIX = " from previous period";

export type AdminReviewsTrendPoint = {
  day: string;
  overall: number;
};

function formatOverallRating(value: number | null): string {
  return value != null ? value.toFixed(1) : "—";
}

function formatPercentValue(value: number | null): string {
  return value != null ? `${value}%` : "—";
}

function formatReviewsCount(value: number): string {
  return value.toLocaleString("en-US");
}

function formatTrendDayLabel(dayKey: string): string {
  try {
    return format(parseISO(dayKey), "MMM d");
  } catch {
    return dayKey;
  }
}

function resolveOverallFromTrendPoint(row: ReviewOverTimeRow): number {
  if (row.overallRating != null) {
    return row.overallRating;
  }

  const categoryValues = row.categories
    ? Object.values(row.categories).filter(
        (value): value is number => value != null,
      )
    : [];

  if (categoryValues.length === 0) {
    return 0;
  }

  return Number(
    (
      categoryValues.reduce((sum, value) => sum + value, 0) /
      categoryValues.length
    ).toFixed(2),
  );
}

function toTableDelta(
  delta: ReviewsKpiDelta,
): Pick<AdminMetricCell, "delta" | "trend"> {
  if (delta.delta === "—") {
    return {};
  }

  return {
    delta: `${delta.delta}${TABLE_DELTA_SUFFIX}`,
    trend: delta.trend,
  };
}

function buildSummaryMetricCell(
  value: string,
  delta: ReviewsKpiDelta,
): AdminMetricCell {
  return {
    value,
    ...toTableDelta(delta),
  };
}

export function mapTenantOverallToSummary(
  response: ReviewOverallResponse | null | undefined,
): ReviewsSummary {
  return mapOverallToSummary(response ?? undefined);
}

export function computeTenantReviewsKpiDeltas(
  current: ReviewOverallResponse | null | undefined,
  previous: ReviewOverallResponse | null | undefined,
): ReviewsKpiDeltas {
  return computeReviewsKpiDeltas(
    mapTenantOverallToSummary(current),
    previous ? mapTenantOverallToSummary(previous) : null,
  );
}

export function mapReviewsHotelRows(
  hotels: TenantReviewsTableRow[],
  currentOverallByHotelId: Map<string, ReviewOverallResponse | null>,
  previousOverallByHotelId: Map<string, ReviewOverallResponse | null>,
  hotelLogosById: Record<string, string | null>,
): AdminHotelMetricRow[] {
  return hotels.map((hotel) => {
    const currentSummary = mapTenantOverallToSummary(
      currentOverallByHotelId.get(hotel.hotelId),
    );
    const previousOverall = previousOverallByHotelId.get(hotel.hotelId);
    const previousSummary = previousOverall
      ? mapTenantOverallToSummary(previousOverall)
      : null;
    const deltas = computeReviewsKpiDeltas(currentSummary, previousSummary);

    return {
      id: hotel.hotelId,
      hotelBrand: hotel.hotelName,
      hotelLogoId: hotelLogosById[hotel.hotelId] ?? null,
      metrics: {
        overallRating: buildSummaryMetricCell(
          formatOverallRating(currentSummary.overallRating),
          deltas.overallRating,
        ),
        positiveNeutral: buildSummaryMetricCell(
          formatPercentValue(currentSummary.positiveNeutralPercent),
          deltas.positiveNeutral,
        ),
        negative: buildSummaryMetricCell(
          formatPercentValue(currentSummary.negativePercent),
          deltas.negative,
        ),
        reviewsCount: buildSummaryMetricCell(
          formatReviewsCount(currentSummary.reviewsCount),
          deltas.reviewsCount,
        ),
      },
    };
  });
}

export function mapTenantOverTimeToTrendPoints(
  response: ReviewOverTimeResponse | null | undefined,
): AdminReviewsTrendPoint[] {
  if (!response?.series.length) {
    return [];
  }

  return response.series.map((row) => ({
    day: formatTrendDayLabel(row.day),
    overall: resolveOverallFromTrendPoint(row),
  }));
}
